import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { LetDirective } from '@ngrx/component';
import { OmniEntryModule } from '@cigna/omni/entry-feature';
import {
  ContentResolver,
  ResourceContentDataAccessModule,
} from '@cigna/shared/angular/resource-content-data-access';
import { LoggingService } from '@cigna/shared/angular/logging-util';
import {
  SharedTimeoutPopupFeatureModule,
  TimeoutPopupConfig,
  timeoutPopupProviders,
} from '@cigna/shared/angular/timeout-popup-feature';

import {
  ChcpShellUiModule,
  UnavailableComponent,
  NotFoundComponent,
  BlockComponent,
} from '@cigna/chcp/shell/ui';
import {
  DisabledPageGuard,
  FeatureToggleGuard,
  RouteGuard,
} from '@cigna/chcp/shared/features-util';
import { ChcpSharedUserProfileDataAccessModule } from '@cigna/chcp/shared/user-profile-data-access';
import { ChcpSharedUserOptionsDataAccessModule } from '@cigna/chcp/shared/user-options-data-access';
import { ChcpSharedEnvironmentUtilModule } from '@cigna/chcp/shared/environment-util';
import {
  ShellEffects,
  SHELL_FEATURE_KEY,
  reducer,
  ShellFacade,
} from '@cigna/chcp/shell/data-access';
import { ChcpRoutes } from '@cigna/chcp/shared/util';
import {
  ContentBundleName,
  manifestAccessorFactory,
} from '@cigna/chcp/shared/content-bundle-util';
import { AuthFacade, EntitlementGuard } from '@cigna/chcp/auth/data-access';
import { Entitlement, LOB_BEHAVIORAL, LOB_MEDBH } from '@cigna/chcp/auth/util';

import { LayoutSecureComponent } from './layout-secure/layout-secure.component';
import { LayoutPublicComponent } from './layout-public/layout-public.component';
import { LayoutPageNotFoundComponent } from './layout-page-not-found/layout-page-not-found.component';
import { LayoutWrapperComponent } from './layout-wrapper/layout-wrapper.component';
import { of } from 'rxjs';

import {
  EvernorthProviderShellUiModule,
  LoginRedirectComponent,
  ImpersonateRedirectComponent,
} from '@cigna/evernorth-provider/shell/ui';

import { SecureLinkRouterComponent } from '@cigna/shared/angular/link-router-feature';
import { LeafLoadingSpinner } from '@cigna/leaf-web/lib/angular';

export const routes: ChcpRoutes = [
  {
    path: '',
    component: LayoutWrapperComponent,
    resolve: {
      sharedTranslation: ContentResolver,
    },
    data: {
      preloadContent: [ContentBundleName.SHARED],
    },
    children: [
      {
        path: '',
        component: LayoutPublicComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'login',
          },
          {
            path: 'login',
            loadChildren: () =>
              import('@cigna/evernorth-provider/login/shell-feature').then(
                (m) => m.EvernorthProviderLoginShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preloadContent: [ContentBundleName.LOGIN],
            },
          },
          {
            path: 'redirect',
            pathMatch: 'full',
            component: LoginRedirectComponent,
            data: {
              preloadContent: [ContentBundleName.REDIRECT],
            },
          },
          {
            path: 'impersonate',
            pathMatch: 'full',
            component: ImpersonateRedirectComponent,
            data: {
              preloadContent: [ContentBundleName.REDIRECT],
            },
          },
          {
            path: 'resources/results/public',
            loadChildren: () =>
              import('@cigna/chcp/resources/search-feature').then(
                (m) => m.ChcpResourcesSearchFeatureModule,
              ),
            data: {
              routeCode: 'resources',
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'registration',
            loadChildren: () =>
              import('@cigna/chcp/registration-feature').then(
                (m) => m.ChcpRegistrationModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preloadContent: [ContentBundleName.REGISTRATION],
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'identity',
            loadChildren: () =>
              import('@cigna/chcp/forgot-identity-feature').then(
                (m) => m.ChcpForgotIdentityModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preloadContent: [ContentBundleName.FORGOT_IDENTITY],
            },
          },
          {
            path: 'mfa',
            loadChildren: () =>
              import('@cigna/chcp/mfa/shell-feature').then(
                (m) => m.ChcpMfaShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preloadContent: [ContentBundleName.MFA],
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'agreement',
            loadChildren: () =>
              import('@cigna/chcp/agreement/shell-feature').then(
                (m) => m.ChcpAgreementShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preloadContent: [ContentBundleName.USER_AGREEMENT],
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'block',
            pathMatch: 'full',
            component: BlockComponent,
          },
          {
            path: 'unavailable',
            component: UnavailableComponent,
          },
        ],
      },
      {
        path: '',
        component: LayoutSecureComponent,
        canActivateChild: [RouteGuard],
        children: [
          {
            path: 'dashboard',
            loadChildren: () =>
              import('@cigna/chcp/dashboard/shell-feature').then(
                (m) => m.ChcpDashboardShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preload: true,
              preloadContent: [ContentBundleName.DASHBOARD],
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'preferences',
            loadChildren: () =>
              import('@cigna/chcp/preferences/shell-feature').then(
                (m) => m.ChcpPreferencesShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preloadContent: [ContentBundleName.SETTINGS],
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'claim',
            loadChildren: () =>
              import('@cigna/chcp/claims/shell-feature').then(
                (m) => m.ChcpClaimsShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preload: true,
              preloadContent: [
                ContentBundleName.CLAIMS,
                ContentBundleName.APPEAL,
              ],
              guards: {
                entitlements: [Entitlement.ClaimsSearch],
              },
            },
            canActivate: [EntitlementGuard, FeatureToggleGuard],
          },
          {
            path: 'patient',
            loadChildren: () =>
              import('@cigna/chcp/patient/shell-feature').then(
                (m) => m.ChcpPatientShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preload: true,
              preloadContent: [
                ContentBundleName.PATIENT,
                ContentBundleName.APPEAL,
                ContentBundleName.DRUGLISTPDF,
                ContentBundleName.DRUGLISTPDL,
              ],
              guards: {
                entitlements: [Entitlement.PatientSearch],
              },
            },
            canActivate: [EntitlementGuard, FeatureToggleGuard],
          },
          {
            path: 'precertification',
            loadChildren: () =>
              import(
                '@cigna/chcp/precertification/precertification-feature'
              ).then((m) => m.ChcpPrecertificationFeatureModule),
            data: {
              pageLayout: 'customFlush',
              pageBg: 'subtle',
              guards: {
                features: ['ftr-chcp-pulsar-team-web-patient-pa-check-submit'],
              },
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'report',
            loadChildren: () =>
              import('@cigna/chcp/reports/shell-feature').then(
                (m) => m.ChcpReportsShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preloadContent: [ContentBundleName.REPORTS],
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'manage-access',
            loadChildren: () =>
              import(
                '@cigna/chcp/working-with-cigna/manage-access-feature'
              ).then((m) => m.ChcpWorkingWithCignaManageAccessFeatureModule),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              title: 'Manage User Access',
              guards: {
                entitlements: [
                  {
                    entitlement: Entitlement.DelegationAccessAndViews,
                    lobs: [LOB_BEHAVIORAL, LOB_MEDBH],
                  },
                ],
                features: [
                  'ftr-chcp-nova-team-web-new-manage-user-invite-flow',
                ],
              },
              preloadContent: [ContentBundleName.MANAGE_ACCESS],
            },
            canActivate: [EntitlementGuard],
          },
          {
            path: 'working-with-cigna',
            redirectTo: 'my-practice',
          },
          {
            path: 'my-practice',
            loadChildren: () =>
              import('@cigna/chcp/working-with-cigna/shell-feature').then(
                (m) => m.ChcpWorkingWithCignaShellFeatureModule,
              ),
            resolve: {
              translation: ContentResolver,
            },
            data: {
              preloadContent: [ContentBundleName.WORKING_WITH_CIGNA],
            },
            canActivate: [FeatureToggleGuard],
          },
          {
            path: 'resources',
            loadChildren: () =>
              // eslint-disable-next-line @nx/enforce-module-boundaries
              import('@cigna/chcp/resources/shell-feature').then(
                (m) => m.ChcpResourcesShellFeatureModule,
              ),
            data: {
              disableNavigation: true,
              routeCode: 'resources',
            },
          },
          {
            path: 'message-center',
            loadChildren: () =>
              import('@cigna/chcp/message-center-feature').then(
                (m) => m.ChcpMessageCenterModule,
              ),
            data: {
              guards: {
                entitlements: [
                  {
                    entitlement: Entitlement.ClaimsSearch,
                    lobs: [LOB_MEDBH, LOB_BEHAVIORAL],
                  },
                  {
                    entitlement: Entitlement.PatientSearch,
                    lobs: [LOB_MEDBH, LOB_BEHAVIORAL],
                  },
                ],
              },
            },
            canActivate: [EntitlementGuard],
          },
          {
            path: 'linkrouter',
            component: SecureLinkRouterComponent,
          },
        ],
      },
      {
        path: '**',
        pathMatch: 'full',
        component: LayoutPageNotFoundComponent,
        data: {
          title: '404 Page Not Found',
          analytics: {
            name: 'Page Not Found',
            section: 'Page Not Found',
            prop13: 'pv-New Page',
          },
        },
        children: [
          {
            path: '',
            component: NotFoundComponent,
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [
    CommonModule,
    LetDirective,
    ChcpShellUiModule,
    OmniEntryModule,
    EvernorthProviderShellUiModule,
    ChcpSharedUserProfileDataAccessModule,
    ChcpSharedUserOptionsDataAccessModule,
    ChcpSharedEnvironmentUtilModule,
    LeafLoadingSpinner,
    StoreModule.forFeature(SHELL_FEATURE_KEY, reducer),
    EffectsModule.forFeature([ShellEffects]),
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking',
      scrollPositionRestoration: 'enabled',
    }),
    ResourceContentDataAccessModule.withConfig({
      factory: manifestAccessorFactory,
      deps: [LoggingService],
    }),
    SharedTimeoutPopupFeatureModule,
  ],
  declarations: [
    LayoutPublicComponent,
    LayoutPageNotFoundComponent,
    LayoutWrapperComponent,
    LayoutSecureComponent,
  ],
  providers: [
    FeatureToggleGuard,
    EntitlementGuard,
    DisabledPageGuard,
    RouteGuard,
  ],
})
export class EvernorthProviderShellFeatureModule {
  static withConfig(
    timeoutPopupConfig: TimeoutPopupConfig,
  ): ModuleWithProviders<EvernorthProviderShellFeatureModule> {
    return {
      ngModule: EvernorthProviderShellFeatureModule,
      providers: timeoutPopupProviders(
        timeoutPopupConfig,
        (shell, auth) => ({
          onLogout: () =>
            shell.logOut({
              variation: 'inactive',
              linkName: 'Logout Timeout',
            }),
          onKeepAlive: () => of(auth.authenticate()),
        }),
        ShellFacade,
        AuthFacade,
      ),
    };
  }
}
